import React, { useState, useMemo, useCallback } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { divisionsFilters } from "../filterConfigurations";

const Divisions = () => {
  const { userPermissions } = useAuth();
  const {
    data: divisions,
    loading,
    error,
  } = useFirestore("Divisions", "DivisionName", "asc");
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(divisions || [], divisionsFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const pageName = "Divisions";
  const canEditDivisions = userPermissions["Divisions"]?.edit;

  const columns = useMemo(
    () => [
      {
        field: "DivisionID",
        headerName: "Division ID",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "DivisionName",
        headerName: "Division Name",
        flex: 2,
        minWidth: 200,
      },
      {
        field: "Active",
        headerName: "Active",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (params.value === true ? "Active" : "Inactive"),
      },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((division, index) => ({
      id: division.id || index, // Use division.id if available, otherwise use index
      ...division,
    }));
  }, [filteredData]);

  // Simple toolbar without search (search is external)
  const CustomToolbar = React.useCallback(() => {
    return (
      <SimpleToolbar
        exportOptions={{
          csvOptions: {
            fileName: "divisions",
            delimiter: ",",
          },
          printOptions: {
            hideFooter: true,
            hideToolbar: true,
          },
        }}
      />
    );
  }, []);

  const handleRowClick = (params) => {
    if (canEditDivisions) {
      setSelectedDivision(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedDivision(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>
          Error fetching divisions:{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={divisionsFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={filterModel}
          collapsible={true}
          title="Divisions Filters"
          resultCount={filteredData?.length || 0}
          totalCount={divisions?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEditDivisions && <Add onRecordAdded={handleRecordAdded} />}
          {canEditDivisions && (
            <EditUpload
              collectionName="Divisions"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={columns.map((col) => col.field)}
              exportData={filteredData}
              booleanFields={["Active"]}
              templateFields={columns.map((col) => col.field)}
            />
          )}
          {canEditDivisions && (
            <ClearCollectionButton collectionName="Divisions" />
          )}
        </Box>
        <Box sx={{ height: "calc(100vh - 200px)", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            slots={{
              toolbar: () => (
                <SimpleToolbar
                  exportColumnOrder={columns.map((col) => col.field)}
                  rows={rows}
                  onBulkActionComplete={handleBulkActionComplete}
                  pageName={pageName}
                  booleanFields={["Active"]}
                />
              ),
            }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            getRowId={(row) => row.id}
            sx={{
              "& .MuiDataGrid-row": {
                cursor: canEditDivisions ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedDivision && canEditDivisions && (
        <Edit
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          divisionDetail={selectedDivision}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Divisions;
